import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUtensils, FaClock, FaStar,
  FaMapMarkerAlt, FaPhoneAlt, FaLeaf,
} from "react-icons/fa";
import { GiKnifeFork, GiChefToque } from "react-icons/gi";

import img1 from "../assets/Resturant/1.jpg";
import img2 from "../assets/Resturant/2.jpg";
import img3 from "../assets/Resturant/IMG_1743.JPG";
import img4 from "../assets/Resturant/4.JPG";
import img5 from "../assets/Resturant/5.jpg";
import img6 from "../assets/Resturant/6.jpg";
import img7 from "../assets/Resturant/7.jpeg";

const galleryImages = [
  { src: img1, caption: "Fine Dining Hall" },
  { src: img2, caption: "Signature Dishes" },
  { src: img3, caption: "Restaurant Interior" },
  { src: img4, caption: "Chef's Special" },
  { src: img5, caption: "Riverside Setting" },
  { src: img6, caption: "Private Dining Room" },
  { src: img7, caption: "Evening Ambiance" },
];

const highlights = [
  { icon: <GiChefToque className="text-2xl text-amber-500" />, label: "Expert Chefs", value: "5-Star Kitchen Team" },
  { icon: <FaStar className="text-2xl text-amber-500" />, label: "Cuisine", value: "Bengali & Continental" },
  { icon: <FaLeaf className="text-2xl text-amber-500" />, label: "Ingredients", value: "Fresh & Locally Sourced" },
  { icon: <FaUtensils className="text-2xl text-amber-500" />, label: "Seating", value: "Indoor & Outdoor" },
];

/* ── Lightbox ── */
function Lightbox({ images, index, onClose, onPrev, onNext, onJump }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[200] flex flex-col bg-black/95"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* ── Top bar ── */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 md:px-6 py-3 bg-black/40 backdrop-blur-sm border-b border-white/10">
          <span className="text-white font-semibold text-base truncate">
            {images[index].caption}
          </span>
          <span className="text-white/60 text-sm font-medium mx-4 whitespace-nowrap">
            {index + 1} / {images.length}
          </span>
          <button
            onClick={onClose}
            className="flex-shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-red-500 text-white flex items-center justify-center transition-colors duration-200"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Image area ── */}
        <div className="flex-1 min-h-0 relative flex items-center justify-center px-14 md:px-20 py-4">
          {/* Prev */}
          <button
            onClick={onPrev}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-amber-500 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm shadow-lg z-10"
            aria-label="Previous"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Image */}
          <AnimatePresence mode="wait">
            <motion.img
              key={index}
              src={images[index].src}
              alt={images[index].caption}
              className="max-h-full max-w-full object-contain rounded-xl shadow-2xl"
              style={{ maxHeight: "calc(100vh - 130px)" }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.2 }}
            />
          </AnimatePresence>

          {/* Next */}
          <button
            onClick={onNext}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-amber-500 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm shadow-lg z-10"
            aria-label="Next"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* ── Thumbnail strip ── */}
        <div className="flex-shrink-0 flex gap-2 overflow-x-auto px-4 py-3 bg-black/40 border-t border-white/10" style={{ scrollbarWidth: "none" }}>
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => onJump(idx)}
              className={`flex-shrink-0 w-14 h-10 md:w-16 md:h-11 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                idx === index
                  ? "border-amber-400 opacity-100 scale-105"
                  : "border-transparent opacity-50 hover:opacity-80 hover:border-white/40"
              }`}
            >
              <img src={img.src} alt={img.caption} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ── Main Component ── */
function Restaurant() {
  const [lightboxIdx, setLightboxIdx] = useState(null);

  return (
    <div className="min-h-screen bg-stone-50">

      {/* ── Hero Banner ── */}
      <div className="relative h-[60vh] min-h-[320px] overflow-hidden">
        <img src={img1} alt="The Purbachal Kitchen" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/45 to-black/80" />
        <div className="absolute inset-0 flex flex-col justify-end items-center text-white text-center px-4 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <GiKnifeFork className="text-amber-400 text-xl" />
              <span className="text-amber-300 uppercase tracking-[0.3em] text-xs font-semibold">
                Chutir Janala Restaurant
              </span>
              <GiKnifeFork className="text-amber-400 text-xl" />
            </div>
            <h1 className="text-4xl md:text-6xl font-fancy drop-shadow-lg mb-2">
              The Purbachal Kitchen
            </h1>
            <p className="text-white/80 text-base md:text-lg max-w-lg mx-auto">
              Authentic flavours crafted with love, served with elegance
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── Restaurant Name & Details... ── */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8 md:gap-10 items-start">

          {/* Left – Description */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-600 uppercase tracking-widest text-xs font-bold">About the Restaurant</span>
            <h2 className="text-3xl md:text-4xl font-fancy text-gray-800 mt-2 mb-1">
              The Purbachal Kitchen
            </h2>
            <p className="text-amber-700 italic text-sm mb-3">
              "Where every meal is a celebration of taste and tradition"
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Step into <strong>The Purbachal Kitchen</strong>, the signature restaurant of Chuti Resort Purbachal. Nestled amid lush greenery with an open riverside view, our restaurant offers a warm, inviting atmosphere that perfectly complements its outstanding cuisine.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Our team of expert chefs curates an exceptional menu that celebrates the rich culinary heritage of Bangladesh, elevated with contemporary techniques. Every dish is prepared using fresh, locally sourced ingredients — from hand-picked vegetables to freshwater fish caught daily.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Whether you are joining us for a leisurely breakfast, a business lunch, or a romantic candlelit dinner, The Purbachal Kitchen promises an unforgettable dining journey for every guest.
            </p>

            {/* Info row */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
                <FaClock className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700">Opening Hours</p>
                  <p className="text-gray-500">Breakfast: 7:00 AM – 10:30 AM</p>
                  <p className="text-gray-500">Lunch: 12:00 PM – 3:00 PM</p>
                  <p className="text-gray-500">Dinner: 7:00 PM – 11:00 PM</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
                <FaMapMarkerAlt className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700">Location</p>
                  <p className="text-gray-500">Ground Floor, Main Building</p>
                  <p className="text-gray-500">Chuti Resort Purbachal</p>
                  <p className="text-gray-500">Purbachal, Dhaka</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
                <FaStar className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700">Capacity</p>
                  <p className="text-gray-500">Indoor: 80 Guests</p>
                  <p className="text-gray-500">Outdoor Terrace: 40 Guests</p>
                  <p className="text-gray-500">Private Room: 20 Guests</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
                <FaPhoneAlt className="text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-700">Reservations</p>
                  <p className="text-gray-500">Walk-ins welcome</p>
                  <p className="text-gray-500">Advance booking advised</p>
                  <p className="text-gray-500">for groups of 10+</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right – Highlight cards + stacked images */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Highlight cards */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {highlights.map((h) => (
                <div key={h.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
                  {h.icon}
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">{h.label}</p>
                    <p className="text-sm font-semibold text-gray-700">{h.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Two stacked preview images */}
            <div className="grid grid-cols-2 gap-3">
              <img
                src={img2}
                alt="Cuisine"
                className="rounded-2xl object-cover w-full h-44 shadow-md cursor-pointer hover:scale-105 transition-transform duration-300"
                onClick={() => setLightboxIdx(1)}
              />
              <img
                src={img3}
                alt="Terrace"
                className="rounded-2xl object-cover w-full h-44 shadow-md mt-6 cursor-pointer hover:scale-105 transition-transform duration-300"
                onClick={() => setLightboxIdx(2)}
              />
              <img
                src={img4}
                alt="Chef Special"
                className="rounded-2xl object-cover w-full h-44 shadow-md -mt-6 cursor-pointer hover:scale-105 transition-transform duration-300"
                onClick={() => setLightboxIdx(3)}
              />
              <img
                src={img5}
                alt="Riverside"
                className="rounded-2xl object-cover w-full h-44 shadow-md cursor-pointer hover:scale-105 transition-transform duration-300"
                onClick={() => setLightboxIdx(4)}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Photo Gallery ── */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-amber-600 uppercase tracking-widest text-xs font-bold">Gallery</span>
          <h2 className="text-3xl md:text-4xl font-fancy text-gray-800 mt-2">A Glimpse Inside</h2>
          <p className="text-gray-400 mt-2 text-sm">Click any image to view full screen</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {/* Large featured */}
          <motion.div
            className="col-span-2 md:col-span-1 row-span-2 overflow-hidden rounded-2xl shadow-lg cursor-pointer group"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            onClick={() => setLightboxIdx(5)}
          >
            <div className="relative h-full">
              <img
                src={img6}
                alt="Private Dining"
                className="w-full h-full min-h-[280px] object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-5">
                <span className="text-white font-medium font-fancy text-lg">Private Dining Room</span>
              </div>
            </div>
          </motion.div>

          {[img1, img2, img3, img4, img5].map((src, i) => (
            <motion.div
              key={i}
              className="overflow-hidden rounded-2xl shadow-md cursor-pointer group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              onClick={() => setLightboxIdx(i)}
            >
              <div className="relative overflow-hidden">
                <img
                  src={src}
                  alt={galleryImages[i].caption}
                  className="w-full h-44 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors duration-300 flex items-end">
                  <p className="text-white text-xs font-medium p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    {galleryImages[i].caption}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Full-width last image */}
          <motion.div
            className="col-span-2 md:col-span-2 overflow-hidden rounded-2xl shadow-md cursor-pointer group"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            onClick={() => setLightboxIdx(6)}
          >
            <div className="relative overflow-hidden">
              <img
                src={img7}
                alt="Evening Ambiance"
                className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent flex items-end">
                <p className="text-white font-fancy font-semibold p-6 text-xl">Evening Ambiance</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Reservation CTA ── */}
      <section className="bg-gradient-to-br from-amber-50 to-amber-100 py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-fancy text-amber-900 mb-2">Reserve Your Table</h3>
            <p className="text-gray-600 mb-5">
              Experience the finest dining at The Purbachal Kitchen. Make a reservation today and let us craft an unforgettable meal just for you.
            </p>
            <a
              href="/contact"
              className="inline-block bg-amber-700 hover:bg-amber-800 text-white font-semibold px-8 py-3 rounded-full transition-colors duration-300 shadow-lg"
            >
              Book a Table
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Lightbox ── */}
      {lightboxIdx !== null && (
        <Lightbox
          images={galleryImages}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => (i - 1 + galleryImages.length) % galleryImages.length)}
          onNext={() => setLightboxIdx((i) => (i + 1) % galleryImages.length)}
          onJump={(idx) => setLightboxIdx(idx)}
        />
      )}
    </div>
  );
}

export default Restaurant;
